<template>
  <section class="analysis-section" aria-labelledby="chart-analysis-title">
    <h2 id="chart-analysis-title" class="section-title">
      <span class="bar"></span>
      Chart Image Analysis
    </h2>

    <div class="top-cards">
      <!-- 🔵 클라우드 카드 -->
      <div class="card cloud-card">
        <div class="cloud-placeholder">
          <div class="cloud-icon">
            <img src="@/assets/img/cloud.svg" alt="클라우드 아이콘" />
          </div>

          <div class="cloud-title">Import from Cloud</div>
          <div class="cloud-desc">Import charts from Hanyang Cloud</div>

          <button class="cloud-btn" @click="handleCloudClick">
            <img
              class="cloud-btn-icon"
              src="@/assets/img/cloud_btn.svg"
              alt=""
            />
            <span>Cloud Load</span>
          </button>
        </div>
      </div>

      <!-- 🟠 업로드 카드 -->
      <div class="card">
        <!-- 업로드 영역: 이미지가 없을 때만 보여줌 -->
        <div
          v-if="uploadedImages.length === 0"
          class="upload-area"
          role="button"
          aria-label="upload local chart images"
          tabindex="0"
          @click="triggerFileInput"
          @keyup.enter.prevent="triggerFileInput"
          @dragover.prevent
          @drop="handleDrop"
        >
          <input
            ref="fileInput"
            type="file"
            accept="image/*"
            multiple
            @change="handleFileSelect"
            style="display: none"
          />

          <div class="upload-placeholder" aria-hidden="true">
            <div class="upload-icon">
              <img src="@/assets/img/upload.svg" alt="" />
            </div>
            <div class="upload-title">Upload File</div>
            <div class="upload-desc1">Click or drag to upload</div>
            <div class="upload-desc2">PNG, JPG, GIF (Max 10MB)</div>
            <div class="upload-link">Import from computer &gt;</div>
          </div>
        </div>

        <!-- 업로드된 이미지 -->
        <div v-if="uploadedImages.length > 0" class="image-gallery">
          <button
            v-for="(image, index) in uploadedImages"
            :key="image.id"
            class="image-item"
            aria-label="select uploaded image for analysis"
            @click="selectImage(image)"
            @keyup.enter.prevent="selectImage(image)"
          >
            <img :src="image.file" class="gallery-image" />
            <button
              @click.stop="removeImage(index)"
              class="remove-btn"
              :aria-label="'image ' + image.name + ' delete button'"
            >
              <span aria-hidden="true">x</span>
            </button>
          </button>

          <button class="clear-btn" @click="clearAllImages">
            Clear All Images
          </button>
        </div>
      </div>
    </div>
  </section>
  <DotCloud v-if="showCloudDialog" @close="showCloudDialog = false" />
</template>

<script>
import DotCloud from "@/components/dialog/DotCloud.vue";
import { checkLogin, getLoginUrl } from "@/utils/auth";

export default {
  components: { DotCloud },
  name: "ChartAnalysisSection",
  emits: ["cloud-click", "image-selected"],

  data() {
    return {
      uploadedImages: [],
      showCloudDialog: false,
    };
  },

  methods: {
    async handleCloudClick() {
      const isLoggedIn = await checkLogin(this.$store);

      if (isLoggedIn) {
        this.showCloudDialog = true;
      } else {
        window.location.href = getLoginUrl();
      }
    },
    triggerFileInput() {
      this.$refs.fileInput.click();
    },

    handleFileSelect(event) {
      this.processFiles(event.target.files);
    },

    handleDrop(event) {
      event.preventDefault();
      this.processFiles(event.dataTransfer.files);
    },

    processFiles(files) {
      Array.from(files).forEach((file) => {
        if (!file.type.startsWith("image/")) return;

        const reader = new FileReader();

        reader.onload = (e) => {
          const imageData = {
            id: `img_${Date.now()}_${Math.random()}`,
            objectType: "Image",
            file: e.target.result,
            name: file.name,
            size: file.size,
            isUploaded: true,
            isAnalyzed: false,
            uploadedAt: new Date().toISOString(),
          };

          this.uploadedImages.push(imageData);
        };

        reader.readAsDataURL(file);
      });
    },

    selectImage(image) {
      this.$emit("image-selected", image);
    },

    removeImage(index) {
      this.uploadedImages.splice(index, 1);
    },

    clearAllImages() {
      this.uploadedImages = [];
    },
  },
};
</script>

<style scoped>
.analysis-section {
  padding: 32px;
}

.section-title {
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 30px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.section-title .bar {
  width: 4px;
  height: 18px;
  background: #f97316;
  border-radius: 2px;
}

.top-cards {
  display: flex;
  gap: 30px;
}

.card {
  flex: 1;
  background: white;
  border-radius: 18px;
  padding: 40px;
  border: 1px solid #e5e7eb;
  text-align: center;
  height: 500px;

  display: flex;
  align-items: center;
  justify-content: center;
}

.icon-circle {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  margin: 0 auto 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
}

.icon-circle.blue {
  background: #e0edff;
}
.icon-circle.orange {
  background: #ffe9dc;
}

.primary-btn {
  margin-top: 20px;
  background: #f97316;
  color: white;
  border: none;
  padding: 10px 22px;
  border-radius: 10px;
  font-weight: 600;
}

.cloud-placeholder {
  min-height: 220px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.cloud-icon {
  width: 64px;
  height: 64px;
  border-radius: 999px;
  background: #eaf2ff;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 6px;
}

.cloud-icon img {
  width: 64px;
  height: 64px;
  object-fit: contain;
}

.cloud-title {
  font-size: 20px;
  font-weight: 700;
  color: #111827;
  margin-top: 4px;
}

.cloud-desc {
  font-size: 13px;
  color: #9ca3af;
}

.cloud-btn {
  margin-top: 14px;
  background: #f97316;
  color: #fff;
  border: none;
  padding: 10px 18px;
  border-radius: 12px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 10px 18px rgba(249, 115, 22, 0.25);
  cursor: pointer;
}

.cloud-btn-icon {
  width: 16px;
  height: 16px;
  object-fit: contain;
}

.upload-area {
  /*border: 2px dashed #e5e7eb;*/
  border-radius: 14px;
  min-height: 220px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background: #fff;
}

.upload-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.upload-icon {
  width: 64px;
  height: 64px;
  border-radius: 999px;
  background: #fff3e8; /* 연한 주황 */
  display: flex;
  align-items: center;
  justify-content: center;
}

.upload-icon img {
  width: 64px;
  height: 64px;
  object-fit: contain;
}

.upload-title {
  font-size: 20px;
  font-weight: 700;
  color: #111827;
  margin-top: 4px;
}

.upload-desc1 {
  font-size: 16px;
  color: #6b7280;
}

.upload-desc2 {
  font-size: 12px;
  color: #6b7280;
  margin-top: -8px;
}

.upload-link {
  font-size: 13px;
  color: #f97316;
  margin-top: 6px;
}

.image-gallery {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.image-item {
  position: relative;
  width: 250px;
  height: 250px;
}

.gallery-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 10px;
}

.remove-btn {
  position: absolute;
  top: -6px;
  right: -6px;
  background: #ef4444;
  border: none;
  color: white;
  border-radius: 50%;
  width: 22px;
  height: 22px;
}

.clear-btn {
  margin-top: 15px;
  background: none;
  border: none;
  color: #f97316;
  font-weight: 600;
  font-size: 16px;
}
</style>
